import SidebarItem from "./SidebarItem";
import { ChevronDown } from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import { colorTokens } from "../../../styles/designTokens";

export default function SidebarSection({
  section,
  isCollapsed,
  onCollapsedSectionSelect,
  onCollapsedHover,
  onCollapsedLeave,
  activePath,
  typography,
  pendingSection,
  onPendingSectionHandled,
  onItemSelect,
}) {
  const role = localStorage.getItem("role");
  const sectionName = section?.name || "";

  const visibleItems = useMemo(() => {
    const items = section?.item || [];
    const sectionRoles = section?.roles || [];
    if (!(role && sectionRoles.includes(role))) return [];
    return items.filter((item) => item.roles.includes(role));
  }, [role, section]);

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const isPathActive = (currentPath, targetPath) => {
    if (!targetPath) return false;
    if (targetPath === "/") return currentPath === "/";
    return (
      currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
    );
  };

  const hasActive = visibleItems.some((item) => {
    if (item.to && isPathActive(activePath, item.to)) return true;
    const children = item.subtitle || [];
    return children.some((s) => s.to && isPathActive(activePath, s.to));
  });

  useEffect(() => {
    if (hasActive) setIsOpen(true);
  }, [hasActive]);

  useEffect(() => {
    if (pendingSection && pendingSection === sectionName) {
      setIsOpen(true);
      onPendingSectionHandled?.();
    }
  }, [pendingSection, sectionName, onPendingSectionHandled]);

  const baseTypographyStyle = useMemo(
    () =>
      typography
        ? {
            fontFamily: typography.fontFamily,
            fontSize: typography.fontSize,
            fontWeight: typography.fontWeight,
          }
        : undefined,
    [typography]
  );

  const activeBackgroundColor =
    colorTokens?.actionSecondarySelected || "#d8d8d8";
  const activeTextColor = colorTokens?.contentDarkPrimary || "#0f172a";
  const collapsedAccentColor =
    colorTokens?.interactionPurpleBase ||
    colorTokens?.actionPrimaryBase ||
    "#6366f1";

  const baseButtonClasses =
    "flex w-full items-center justify-between px-2 py-2 font-semibold uppercase tracking-wide rounded-lg transition-colors";
  const inactiveButtonClasses = "text-slate-500 hover:bg-slate-50";
  const activeButtonClasses = "shadow-sm";
  const activeButtonStyle = baseTypographyStyle
    ? {
        ...baseTypographyStyle,
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: activeBackgroundColor,
        color: activeTextColor,
      }
    : {
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: activeBackgroundColor,
        color: activeTextColor,
      };
  const inactiveButtonStyle = baseTypographyStyle
    ? { ...baseTypographyStyle, fontSize: "12px", fontWeight: 500 }
    : { fontSize: "12px", fontWeight: 500 };

  if (visibleItems.length === 0) return null;

  if (isCollapsed) {
    const handleEnter = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const filteredItems = visibleItems.map((item) => ({
          ...item,
          subtitle: (item.subtitle || []).filter(
            (s) => !s.roles || s.roles.includes(role)
          ),
        }));
        onCollapsedHover({
          sectionName,
          items: filteredItems,
          anchor: {
            top: rect.top + rect.height / 2,
            left: rect.right + 12,
          },
        });
      }
    };

    const handleLeave = () => {
      onCollapsedLeave();
    };

  const handleCollapsedClick = () => {
    handleLeave();
    onCollapsedSectionSelect?.(sectionName);
  };

    return (
      <div className="relative w-full flex justify-center">
        <button
          ref={triggerRef}
          onClick={handleCollapsedClick}
          className={`relative flex flex-col h-auto w-full items-center justify-center rounded-lg px-2 py-3 transition-colors text-[11px] leading-tight ${
            hasActive ? `${activeButtonClasses} collapsed-active` : inactiveButtonClasses
          }`}
          title={sectionName}
          style={
            hasActive
              ? { ...activeButtonStyle, "--sidebar-active-accent": collapsedAccentColor }
              : inactiveButtonStyle
          }
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {hasActive && (
            <span
              aria-hidden="true"
              className="absolute left-0 top-1/2 h-6 w-1.5 -translate-y-1/2 rounded-r-full bg-[var(--sidebar-active-accent,#6366f1)]"
            />
          )}
          <span
            className="text-lg"
            style={{ color: hasActive ? activeTextColor : undefined }}
          >
            {sectionName.charAt(0)}
          </span>
          <span className="text-[9px] font-medium uppercase tracking-wide truncate w-full text-center">
            {sectionName}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={isCollapsed ? "px-1" : ""}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${baseButtonClasses} ${
          hasActive ? activeButtonClasses : inactiveButtonClasses
        }`}
        style={
          hasActive
            ? { ...activeButtonStyle, "--sidebar-active-accent": collapsedAccentColor }
            : inactiveButtonStyle
        }
      >
        {sectionName}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`mt-1 ml-2 border-l pl-3 flex flex-col gap-1 transition-all ${
          isOpen
            ? "max-h-[800px] opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        {visibleItems.map((item, i) => (
          <SidebarItem
            key={i}
            item={item}
            role={role}
            activePath={activePath}
            typography={typography}
            onItemSelect={onItemSelect}
          />
        ))}
      </div>
    </div>
  );
}
